/**
 * Echo Hunt -- live model-metadata lookup (2026-10-21).
 *
 * The thin, network/cache layer behind `ModelCapabilities.ts`. It asks OpenRouter what a model supports so the
 * backend can avoid the `"Reasoning is mandatory for this endpoint and cannot be disabled"` rejection, and so
 * the Settings tab can offer a reasoning dropdown that only lists valid choices.
 *
 * ## Anthropic-direct has no equivalent endpoint
 *
 * There is no per-model metadata API on the direct Anthropic path, so `GetModelCapabilities` returns
 * `UnknownCapabilities` there and the call goes out exactly as it always did. The backend additionally catches
 * a mandatory-reasoning rejection and retries once with a reasoning effort (`IsReasoningMandatoryError`), so the
 * feature degrades to "it still works, one call later" rather than to a hard failure.
 *
 * ## Caching
 *
 * Results are cached per model id for the session: the metadata is static, the Settings tab reads it on every
 * render, and a network round-trip per row per render would be both slow and rude. A FAILED fetch is cached too,
 * as `UnknownCapabilities` -- retrying on every keystroke would hammer the endpoint. `ClearModelCapabilitiesCache`
 * exists so the panel's "reload" action can force a fresh read.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §16.0.
 */
import {store} from "../Store/index.js";
import {LLMApi} from "../Store/main/settings.js";
import {GetOpenRouterModelInfo} from "../Utils/AI/LLMs/OpenRouter.js";
import {ModelCapabilities, ParseModelCapabilities, UnknownCapabilities} from "./ModelCapabilities.js";

const cache = new Map<string, ModelCapabilities>();
const inFlight = new Map<string, Promise<ModelCapabilities>>();

/** Drops the cache, so the next read refetches. */
export function ClearModelCapabilitiesCache(): void {
	cache.clear();
	inFlight.clear();
}

/** True when metadata can be fetched at all (only OpenRouter exposes a per-model endpoint). */
export function ModelCapabilitiesAvailable(): boolean {
	return store.main.settings.llm_mainAPI == LLMApi.openRouter;
}

/** The capabilities of one model, from cache when possible. Never throws: any failure resolves to
 * `UnknownCapabilities`, which makes the caller behave exactly as it did before this feature existed. */
export async function GetModelCapabilities(model: string): Promise<ModelCapabilities> {
	const id = model.trim();
	if (id.length == 0) return UnknownCapabilities(id);

	const cached = cache.get(id);
	if (cached != null) return cached;

	if (!ModelCapabilitiesAvailable()) {
		const unknown = UnknownCapabilities(id);
		cache.set(id, unknown);
		return unknown;
	}

	// coalesce concurrent reads of the same model (the Settings tab renders one row per role, and several
	// rows may share the global model)
	const existing = inFlight.get(id);
	if (existing != null) return existing;

	const promise = GetOpenRouterModelInfo(id)
		.then(body=>{
			const parsed = ParseModelCapabilities(id, body);
			cache.set(id, parsed);
			return parsed;
		})
		.catch(ex=>{
			console.warn(`Could not fetch model metadata for "${id}" (falling back to "no reasoning info"):`, ex);
			const unknown = UnknownCapabilities(id);
			cache.set(id, unknown);
			return unknown;
		})
		.finally(()=>{
			inFlight.delete(id);
		});

	inFlight.set(id, promise);
	return promise;
}
